import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MdSearch,
  MdClear,
  MdDownload,
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
} from "react-icons/md";

import { fetchOrganizerOffers, deleteOffer } from "api/offer.api";
import OffersTable from "components/offers/OffersTable";
import BulkImportButton from "components/common/BulkImportButton";


export default function Offers() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );

  const formatDateTime = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

  const userId = "69579c6190a653b640e65639";


  const fetchData = async () => {
    const res = await fetchOrganizerOffers(
      selectedOrganizer || undefined
    );

    setData(Array.isArray(res.data?.data) ? res.data.data : []);
  };

  useEffect(() => {
    fetchData();
  }, [selectedOrganizer]);

  useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
    };

    window.addEventListener(
      "companyChanged",
      handleOrganizerChange
    );

    return () =>
      window.removeEventListener(
        "companyChanged",
        handleOrganizerChange
      );
  }, []);

  const handleDelete = async (id) => {
    await deleteOffer(id, {
      is_deleted: true,
      deleted_by: userId,
    });

    fetchData();
  };


  const filteredData = data.filter(
    (item) =>
      item.title?.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase())
  );


  const totalPages = Math.ceil(filteredData.length / limit);

  const paginatedData = filteredData.slice(
    (page - 1) * limit,
    page * limit
  );

  const escapeCSV = (val) => {
  if (val === null || val === undefined) return "-";
  const str = String(val);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};


  const exportCSV = () => {
    const headers = [
      "Exhibitor",
      "Offer Title",
      "Target Product",
      "Start Date",
      "End Date",
      "Status",
    ];

    const rows = filteredData.map((o) => [
      o.exhibitor_id?.title,
      o.title,
      o.target_products?.length
      ? o.target_products.map((p) => p.product_name).join(", ")
      : "-",
      o.start_date ? formatDateTime(o.start_date) : "-",
      o.end_date ? formatDateTime(o.end_date) : "-",
      o.status,
    ]);

    const csvContent =
    "data:text/csv;charset=utf-8," +
    [headers, ...rows]
      .map((r) => r.map(escapeCSV).join(","))
      .join("\n");

  const link = document.createElement("a");
  link.href = encodeURI(csvContent);
  link.download = "offers.csv";
  link.click();
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <MdSearch className="absolute left-3 top-2.5 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search offers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10 pr-8 py-2 border rounded-md text-sm w-64 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />

            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <MdClear />
              </button>
            )}
          </div>

          <button
            onClick={exportCSV}
            className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-lg"
          >
            <MdDownload />
            CSV
          </button>
        </div>
       <div className="flex items-center gap-2">
          <BulkImportButton module="offers" label="Import Offers" onSuccess={fetchData} companyId={selectedOrganizer} />
        <Link
          to="/admin/offers/create"
          className="bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          Add Offer
        </Link>
      </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border">
        <OffersTable
          data={paginatedData}
          onRefresh={fetchData}
          onDelete={handleDelete}
        />

        <div className="flex justify-between items-center px-4 py-3 border-t text-sm">
          <span className="text-gray-500">
            {filteredData.length} total
          </span>

          <div className="flex items-center gap-1">
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="border rounded-md px-2 py-2 text-sm"
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>

            <button
              disabled={page === 1}
              onClick={() => setPage(1)}
              className="p-2 border rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <MdFirstPage />
            </button>

            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="p-2 border rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <MdChevronLeft />
            </button>

            <span className="px-3 font-medium">
              {page} / {totalPages || 1}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="p-2 border rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <MdChevronRight />
            </button>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
              className="p-2 border rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <MdLastPage />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
